import {create} from "zustand";
import {MapReleaseStoreState} from "./models/MapReleaseStoreState";
import {getConsensusApprovalLink, getStationSimulationSummary, getConsensusApprovalStatus, getMapPackBedrockSummary, getMapPackValidationSummary, getStationRecostingSummary, releaseMapPack, getReleaseStatus, getMCMLink, checkMCMExists} from "../utils/mapReleaseDashboard/mapReleaseApiCall";
import {defaultVisibleContentOptionsFromColumns} from "../utils/tableConfig";
import {
    STATION_RECOSTING_SUMMARY_DEFAULT_VISIBLE_COLUMNS
} from "../utils/mapReleaseDashboard/stationRecostingSummaryTableConfig";
import {
    STATION_SIMULATION_SUMMARY_TABLE_COLUMN_DEFINITIONS
} from "../utils/mapReleaseDashboard/stationSimulationSummaryTableConfig";
import {
    MAP_PACK_VALIDATION_SUMMARY_DEFAULT_VISIBLE_COLUMNS
} from "../utils/mapReleaseDashboard/mapPackValidationSummaryTableConfig";
import { MAP_PACK_RELEASE_SUMMARY_TABLE_COLUMN_DEFINITIONS} 
from "../utils/mapReleaseDashboard/mapPackReleaseSummaryTableConfig";
import { processReleaseOutput, reformatBedrockSummary, reformatReleaseMetricsJSON } from "../utils/mapReleaseDashboard/mapReleaseDataUtils";
import { mapPnfTotals, pnfCount, stationPnfTotals } from "../utils/mapReleaseDashboard/recostPnfMetrics";
import { latencyValue, mapLatencyChanges, stationLatencyChanges } from "../utils/mapReleaseDashboard/recostLatencyMetrics";

/**
 * Loads the map pack classifications from localStorage
 */
const MAP_PACK_CLASSIFICATIONS_KEY = "MAP_PACK_CLASSIFICATIONS";
const loadMapPackClassifications = (): Record<string, string> => {
    const saved = localStorage.getItem(MAP_PACK_CLASSIFICATIONS_KEY);
    return saved ? JSON.parse(saved) : {};
};


export const useMapReleaseStore =
    create<MapReleaseStoreState>((set: any, get: any) => ({
        currentRegion: "NA", // Default region
        setCurrentRegion: (region: string) => {
            console.log("Region: " + region)
            set({ currentRegion: region });
        },

        mcmLink: "",

        /**
         * Fetches the MCM link
         * @param requestBody json body for MCM lambda
         */
        fetchMCMLink: async (requestBody: string) => {
            getMCMLink(requestBody).then((link) => {
                console.log("MCM link: " + link)
                get().setMCMLink(link);
            });
        },

        /**
         * Sets MCM link in the store
         * @param link MCM link
         */
        setMCMLink: (link: any) => {
            if (link === null || link[0] === undefined) {
                set({
                    mcmLink: null
                });
            } else {
                set({
                    mcmLink: link
                });
            }
        },

        /**
         * Retrieves the MCM link
         * @returns link
         */
        getMCMLink: () => {
            return get().mcmLink;
        },

        releaseStatus: "",
        releaseStatusLoaded: false,

        /**
         * Fetches mappack release status data
         * @param region region of mappack
         */
        fetchReleaseStatus: async (region: string) => {
            set({releaseStatusLoaded: false});
            getReleaseStatus(region).then((status) => {
                get().setReleaseStatus(status);
            });

        },

        /**
         * Sets release status in the store
         * @param status release status
         */
        setReleaseStatus: (status: any) => {
            set({
                releaseStatus: status,
                releaseStatusLoaded: true
            });
        },

        /**
         * Retrieves all the release status data
         * @returns release status data
         */
        getReleaseStatus: () => {
            return get().releaseStatus;
        },

         /**
         * Returns if release status information is loaded
         * @returns true or false
         */
        isReleaseStatusLoaded: () => {
            return get().releaseStatusLoaded;
        },


        approvalStatus: [],
        approvalStatusLoaded: false,

        /**
         * Fetches consensus approval status data
         * @param region region of mappack
         */
        fetchConsensusApprovalStatus: async (region: string) => {
            set({approvalStatusLoaded: false});
            
            try {
                const status = await getConsensusApprovalStatus(region);
                get().setConsensusApprovalStatus(status);
            } catch (error) {
                console.error("Error in fetchConsensusApprovalStatus:", error);
                get().setConsensusApprovalStatus([]);
            }
        },

        /**
         * Sets consensus approval status in the store
         * @param status approval status
         */
        setConsensusApprovalStatus: (status: any) => {
            // Ensure status is always an array
            let statusArray;
            if (Array.isArray(status)) {
                statusArray = status;
            } else if (status && typeof status === 'object') {
                statusArray = [status];
            } else if (status) {
                statusArray = [status];
            } else {
                statusArray = [];
            }
            
            set({
                approvalStatus: statusArray,
                approvalStatusLoaded: true
            });
        },
        
        /**
         * Retrieves all the consensus approval status data
         * @returns approval status data
         */
        getConsensusApprovalStatus: () => {
            return get().approvalStatus;
        },

        /**
         * Returns if consensus approval status information is loaded
         * @returns true or false
         */
        isConsensusApprovalStatusLoaded: () => {
            return get().approvalStatusLoaded;
        },
        
        consensusLink: "",

        /**
         * Fetches the consensus approval link
         * @param requestBody json body for consensus lambda
         */
        fetchConsensusApprovalLink: async (requestBody: string) => {
            getConsensusApprovalLink(requestBody).then((link) => {
                console.log("consensus link: " + link)
                get().setConsensusApprovalLink(link);
            });
        },

        /**
         * Sets consensus approval link in the store
         * @param link consensus approval link
         */
        setConsensusApprovalLink: (link: any) => {
            if (link === null || link[0] === undefined) {
                set({
                    consensusLink: null
                });
            } else {
                set({
                    consensusLink: link
                });
            }
        },

        /**
         * Retrieves the consensus approval link
         * @returns link
         */
        getConsensusApprovalLink: () => {
            return get().consensusLink;
        },

        releaseLambdaOutput: "",
        
        /**
         * Fetches release lambda output (for mappack release)
         * @param requestBody json body for release lambda
         */
        fetchReleaseLambdaOutput: async (requestBody: string) => {
            releaseMapPack(requestBody).then((releaseData) => {
                get().setReleaseLambdaOutput(processReleaseOutput(releaseData));
            });
        },

        /**
         * Sets the release lambda output in the store
         * @param releaseData json body for release lambda
         */
        setReleaseLambdaOutput: (releaseData: any) => {
            if (releaseData === null || releaseData[0] === undefined) {
                set({
                    releaseLambdaOutput: null
                });
            } else {
                set({
                    releaseLambdaOutput: releaseData
                });
            }
        },

        /**
         * Gets the release lambda output
         * @returns lambda output
         */
        getReleaseLambdaOutput: () => {
            return get().releaseLambdaOutput;
        },

        mapPackBedrockSummary: [],
        mapPackBedrockClassification: loadMapPackClassifications(), // saved classified mappacks locally
        mapPackBedrockStations: [], 
        mapPackBedrockSummaryLoaded: false,
        invalidMapPackBedrockSummary: false,
        mapPackBedrockRetryCount: 0,
        mapPackBedrockMaxRetries: 3,

        /**
         * Retrieves the bedrock summary
         * @returns bedrock summary
         */
        getMapPackBedrockSummary: () => {
            return get().mapPackBedrockSummary;
        },

        /**
         * Retrieves the bedrock classification (no breach, SPOC adjustment, breach)
         * @param mapPackVersion mappack version
         * @returns classification
         */
        getMapPackBedrockClassification: (mapPackVersion: string) => {
            return get().mapPackBedrockClassification[mapPackVersion] || "";
        },

        /**
         * Retrieves the bedrock violated stations
         * @returns stations
         */
        getMapPackBedrockStations: () => {
            return get().mapPackBedrockStations;
        },

        /**
         * Retrieves if bedrock summary is invalid or not
         * @returns true or false
         */
        getInvalidMapPackBedrockSummary: () => {
            return get().invalidMapPackBedrockSummary;
        },

        /**
         * Sets the mappack bedrock summary in the store
         * @param bedrockData bedrock summary
         * @param mapPackVersion mappack version
         */
        setMapPackBedrockSummary: (bedrockData: any, mapPackVersion: string) => {
            const [summary, stations, classification] = reformatBedrockSummary(bedrockData);
            if (bedrockData === null || bedrockData[0] === undefined) {
                set({
                    mapPackBedrockSummary: [],
                    invalidMapPackBedrockSummary: true
                });
            } else {
                const updatedClassifications = {
                    ...get().mapPackBedrockClassification,
                    [mapPackVersion]: classification
                };
                set({
                    mapPackBedrockSummary: summary,
                    mapPackBedrockClassification: updatedClassifications,
                    mapPackBedrockStations: stations,
                    invalidMapPackBedrockSummary: false
                });
                saveMapPackClassifications(
                    updatedClassifications
                );
            }
        },

        /**
         * Fetches mappack bedrock summary with error handling and retrying functionality
         * @param requestBody json request body
         * @param mapPackVersion version of mappack
         */
        fetchMapPackBedrockSummary: async (requestBody: string, mapPackVersion: string) => {
            set({mapPackBedrockSummaryLoaded: false});
            getMapPackBedrockSummary(requestBody).then((bedrockData) => {
                get().setMapPackBedrockSummary(bedrockData, mapPackVersion);
                set({mapPackBedrockSummaryLoaded: true,
                    mapPackBedrockRetryCount: 0
                 });
            }).catch(error => {
                console.error("Failed to fetch Bedrock summary:", error);

                if (get().mapPackBedrockRetryCount < get().mapPackBedrockMaxRetries) {
                    set({ mapPackBedrockRetryCount: get().mapPackBedrockRetryCount + 1 });
                    
                    console.log(`Retrying... Attempt ${get().mapPackBedrockRetryCount} of ${get().mapPackBedrockMaxRetries}`);
                    const delay = Math.min(1000 * Math.pow(2, get().mapPackBedrockRetryCount), 10000);
                    setTimeout(() => {
                        get().fetchMapPackBedrockSummary(requestBody, mapPackVersion);
                    }, delay);
                    console.log(`Retrying in ${delay}ms...`);
                } else {
                    console.error(`Max retries (${get().mapPackBedrockMaxRetries + 1}) reached. Giving up.`);
                    set({
                        mapPackBedrockSummary: [],
                        invalidMapPackBedrockSummary: true,
                        mapPackBedrockSummaryLoaded: false,
                        mapPackBedrockRetryCount: 0 
                    });
                }
            });
        },
        
        /**
         * Checks if mappack bedrock summary is loaded
         * @returns true or false
         */
        isMapPackBedrockLoaded: () => {
            return get().mapPackBedrockSummaryLoaded;
        },

        mapPackReleaseSummary: [],
        mapPackReleaseSummaryLoaded: false,
        invalidMapPackReleaseSummary: false,

        mapPackReleaseSummaryTablePreferences: {
            pageSize: 5,
            visibleContent: defaultVisibleContentOptionsFromColumns(
                MAP_PACK_RELEASE_SUMMARY_TABLE_COLUMN_DEFINITIONS
            ),
        },

        /**
         * Sets parameters in the global validationTable preferences.
         *
         * @param detail the collection of parameters to modify in the existing preferences
         */
        setMapPackReleaseSummaryTablePreferences: (detail: any) => {
            set({
                mapPackReleaseSummaryTablePreferences: {
                    ...get().mapPackReleaseSummaryTablePreferences,
                    ...detail,
                },
            });
            saveDefaultPreferences(
                get().mapPackReleaseSummaryTablePreferences,
                PreferencesType.MapPackReleaseSummary
            );
        },

        /**
         * Retrieves the fetched mappack release summary
         * @returns release summary
         */
        getMapPackReleaseSummary: () => {
            return get().mapPackReleaseSummary;
        },

        /**
         * Returns if mappack release summary is invalid or not
         * @returns true or false
         */
        getInvalidMapPackReleaseSummary: () => {
            return get().invalidMapPackReleaseSummary;
        },

        /**
         * Checks if mappack release summary is loaded
         * @returns true or false
         */
        isMapPackReleaseLoaded: () => {
            return get().mapPackReleaseSummaryLoaded;
        },

        /**
         * Sets mappack release summary in the store
         * @param releaseData data for release management table
         */
        setMapPackReleaseSummary: (releaseData: any) => {
            if (releaseData === null || releaseData[0] === undefined) {
                set({
                    mapPackReleaseSummary: [],
                    invalidMapPackReleaseSummary: true
                });
            } else {
                set({
                    mapPackReleaseSummary: releaseData,
                    invalidMapPackReleaseSummary: false
                });
            }
        },

        /**
         * Fetches mappack summary for the release management table
         * @param region region of mappack
         */
        fetchMapPackReleaseSummary: async (region: string) => {
            set({mapPackReleaseSummaryLoaded: false});
            getMapPackValidationSummary(region).then((releaseData) => {
                get().setMapPackReleaseSummary(reformatReleaseMetricsJSON(releaseData));
                set({mapPackReleaseSummaryLoaded: true });
            });
        },


        stationRecostingHeaders: [],
        stationRecostingSummary: [],
        stationRecostingLoaded: false,
        invalidStationRecostingSummary: false,

        stationRecostingSummaryTablePreferences: {
            pageSize: 30,
            visibleContent: STATION_RECOSTING_SUMMARY_DEFAULT_VISIBLE_COLUMNS,
        },

        /**
         * Sets parameters in the global validationTable preferences.
         *
         * @param detail the collection of parameters to modify in the existing preferences
         */
        setStationRecostingSummaryTablePreferences: (detail: any) => {
            set({
                stationRecostingSummaryTablePreferences: {
                    ...get().stationRecostingSummaryTablePreferences,
                    ...detail,
                },
            });
            saveDefaultPreferences(
                get().stationRecostingSummaryTablePreferences,
                PreferencesType.StationRecostingSummary
            );
        },

        /**
         * Returns whether the station recosting summary is incorrect
         * @returns boolean of if data is incorrect
         */
        getInvalidStationRecostingSummary: () => {
            return get().invalidStationRecostingSummary;
        },

        /**
         * Gets the headers of the station recosting summary (column names)
         * @returns headers of the station recosting summary
         */
        getStationRecostingHeaders: () => {
            return get().stationRecostingHeaders;
        },

        /**
         * Retrieves if aggregated metrics are done fetching
         */
        isStationRecostingLoaded: () => {
            return get().stationRecostingLoaded;
        },

        /**
         * Returns the station recosting summary data
         * @returns aggregated station recosting summary data
         */
        getStationRecostingData: () => {
            return get().stationRecostingSummary;
        },

        /**
         * Sets the station recosting summary data in the store
         * @param recostingData recosting data fetched
         */
        setStationRecostingSummary: (recostingData: any) => {
            if (recostingData === null || recostingData[0] === undefined) {
                set({
                    stationRecostingSummary: [],
                    invalidStationRecostingSummary: true,
                    stationRecostingHeaders: []
                });
            } else {
                const summary = recostingData.map((item: any) => ({ ...item, ...stationPnfTotals(item), ...stationLatencyChanges(item) }));
                // Union raw keys before appending derived fields: an older first row must not hide
                // fields present on later rows. Export still includes every loaded row.
                const headers = Array.from(new Set<string>(recostingData.flatMap((item: any) => Object.keys(item))));
                set({
                    stationRecostingSummary: summary,
                    invalidStationRecostingSummary: false,
                    stationRecostingHeaders: Array.from(new Set([
                        ...headers, "totalPnfCountControl", "totalPnfCountCandidate", "pnfNetChange",
                        "latencyP50ChangePercent", "latencyP99ChangePercent"
                    ]))
                });
            }
        },

        /**
         * Fetches station recosting summary from the API
         */
        fetchStationRecostingSummary: async (summaryId: string | null ) => {
            set({stationRecostingLoaded: false});
            getStationRecostingSummary(summaryId).then((recostingData) => {
                get().setStationRecostingSummary(recostingData);
                set({stationRecostingLoaded: true });
            });
        },

        // map pack validation summary
        mapPackValidationHeaders: [],
        mapPackValidationSummary: [],
        mapPackValidationLoaded: false,
        invalidMapPackValidationSummary: false,
        currentValidationRegion: "",

        mapPackValidationSummaryTablePreferences: {
            pageSize: 20,
            visibleContent: MAP_PACK_VALIDATION_SUMMARY_DEFAULT_VISIBLE_COLUMNS,
        },

        /**
         * Sets parameters in the global validationTable preferences.
         *
         * @param detail the collection of parameters to modify in the existing preferences
         */
        setMapPackValidationSummaryTablePreferences: (detail: any) => {
            set({
                mapPackValidationSummaryTablePreferences: {
                    ...get().mapPackValidationSummaryTablePreferences,
                    ...detail,
                },
            });
            saveDefaultPreferences(
                get().mapPackValidationSummaryTablePreferences,
                PreferencesType.MapPackValidationSummary
            );
        },

        /**
         * Returns whether the station recosting summary is incorrect
         * @returns boolean of if data is incorrect
         */
        getInvalidMapPackValidationSummary: () => {
            return get().invalidMapPackValidationSummary;
        },

        /**
         * Gets the headers of the station recosting summary (column names)
         * @returns headers of the station recosting summary
         */
        getMapPackValidationHeaders: () => {
            return get().mapPackValidationHeaders;
        },

        /**
         * Retrieves if aggregated metrics are done fetching
         */
        isMapPackValidationLoaded: () => {
            return get().mapPackValidationLoaded;
        },

        /**
         * Returns the station recosting summary data
         * @returns aggregated station recosting summary data
         */
        getMapPackValidationData: () => {
            return get().mapPackValidationSummary;
        },

        /**
         * Sets the station recosting summary data in the store
         * @param recostingData recosting data fetched
         */
      
        setMapPackValidationSummary: (recostingData: any) => {
            if (recostingData === null || recostingData[0] === undefined) {
                set({
                    mapPackValidationSummary: [],
                    invalidMapPackValidationSummary: true,
                    mapPackValidationHeaders: []
                });
            } else {
                const validationData = recostingData.map((item: any) => ({
                    mapPack: item.mapPack || '',
                    mapPackControl: item.mapPackControl || '',
                    mwoWorkflowType: item.mwoWorkflowType || '',
                    recostingDataSource: item.recostingDataSource || '',
                    trecsRecostingLink: item.trecsRecostingLink || '',
                    region: item.region || '',
                    summaryId: item.summaryId || '',
                    rsmVersion: item.rsmVersion || '',
                    vehicleType: item.vehicleType || '',
                    mwoBasemapRefresh: item.mwoBasemapRefresh || false,
                    // The three fields below keep null rather than defaulting to 0. A summary row omits a
                    // derived ratio when its denominator was zero, and omits a counter that did not exist
                    // when the build ran; defaulting either to 0 would report "no change" or "no failures"
                    // for a measurement that was never taken. The table config decides how each is shown.
                    pnfPercentIncrease: item.pnfPercentIncrease ?? null,
                    dpmo: item.dpmo || 0,
                    dpmoChange: item.dpmoChange ?? null,
                    dpmoControl: item.dpmoControl || 0,
                    failedTripCount: item.failedTripCount || 0,
                    failedTripCountControl: item.failedTripCountControl || 0,
                    summaryStationCount: item.summaryStationCount || 0,
                    recostingStationCount: item.recostingStationCount || 0,
                    stationCount: item.summaryStationCount ?? item.recostingStationCount ?? 0,
                    onZonePnfFailureCountControl: item.onZonePnfFailureCountControl ?? null,
                    onZonePnfFailureCountExperiment: item.onZonePnfFailureCountExperiment ?? null,
                    // Null rather than '' for the same reason as the fields above. An absent verdict means
                    // the recost gate never recorded one, which the table shows as "Not evaluated"; '' would
                    // render as an empty cell and read as though the gate were happy.
                    recostGateVerdict: item.recostGateVerdict ?? null,
                    recostGateReason: item.recostGateReason ?? null,
                    stemPnfFailureCount: pnfCount(item.stemPnfFailureCount),
                    stemPnfFailureCountControl: pnfCount(item.stemPnfFailureCountControl),
                    stemTransitTimeBreachingStationCount: item.stemTransitTimeBreachingStationCount || 0,
                    transitTimeBreachingStationCount: item.transitTimeBreachingStationCount || 0,
                    tripCount: item.tripCount || 0,
                    stemSnapFailureCount: item.stemSnapFailureCount || 0,
                    stemSnapFailureCountControl: item.stemSnapFailureCountControl || 0,
                    createdAtEpochSecs: item.createdAtEpochSecs || 0,
                    CreatedAtEpochSecsSimulations: item.CreatedAtEpochSecsSimulations || 0,
                    totalSimulationStationIds: item.totalSimulationStationIds || 0,
                    trecsSimulationLink: item.trecsSimulationLink || '',
                    dpphBreachCount: item.dpphBreachCount || 0,
                    sprBreachCount: item.sprBreachCount || 0,
                    averageCurrentSpr: item.averageCurrentSpr || 0,
                    averageDpphDelta: item.averageDpphDelta || 0,
                    averageOriginalSpr: item.averageOriginalSpr || 0,
                    averageSprDelta: item.averageSprDelta || 0,
                    simulationSummaryId: item.simulationSummaryId || 0,
                    averageCurrentDpph: item.averageCurrentDpph || '',
                    averageOriginalDpph: item.averageOriginalDpph || '',
                    // Optional latency measurements must not default to zero on historical summaries.
                    clientLatencyP50ControlMs: latencyValue(item.clientLatencyP50ControlMs),
                    clientLatencyP50CandidateMs: latencyValue(item.clientLatencyP50CandidateMs),
                    clientLatencyP99ControlMs: latencyValue(item.clientLatencyP99ControlMs),
                    clientLatencyP99CandidateMs: latencyValue(item.clientLatencyP99CandidateMs),
                    clientLatencyRecordCount: latencyValue(item.clientLatencyRecordCount),
                    clientLatencyPairedCount: latencyValue(item.clientLatencyPairedCount),
                    ...mapLatencyChanges(item),
                    // Calculate from the original API counters, before any defaults can mask absence.
                    ...mapPnfTotals(item),
                    increasedTotalPnfStationCount: pnfCount(item.increasedTotalPnfStationCount)
                }));

                set({
                    mapPackValidationSummary: validationData,
                    invalidMapPackValidationSummary: false,
                    mapPackValidationHeaders: Object.keys(validationData[0])
                });
            }
        },

        stationSimulationHeaders: [],
        stationSimulationSummary: [],
        stationSimulationLoaded: false,
        invalidStationSimulationSummary: false,

        stationSimulationSummaryTablePreferences: {
            pageSize: 30,
            visibleContent: defaultVisibleContentOptionsFromColumns(
                STATION_SIMULATION_SUMMARY_TABLE_COLUMN_DEFINITIONS
            ),
        },

        setStationSimulationSummaryTablePreferences: (detail: any) => {
            set({
                stationSimulationSummaryTablePreferences: {
                    ...get().stationSimulationSummaryTablePreferences,
                    ...detail,
                },
            });
            saveDefaultPreferences(
                get().stationSimulationSummaryTablePreferences,
                PreferencesType.StationSimulationSummary
            );
        },

        getInvalidStationSimulationSummary: () => {
            return get().invalidStationSimulationSummary;
        },

        getStationSimulationHeaders: () => {
            return get().stationSimulationHeaders;
        },

        isStationSimulationLoaded: () => {
            return get().stationSimulationLoaded;
        },

        getStationSimulationData: () => {
            return get().stationSimulationSummary;
        },

        setStationSimulationSummary: (simulationData: any) => {
            if (simulationData === null || simulationData[0] === undefined) {
                set({
                    stationSimulationSummary: [],
                    invalidStationSimulationSummary: true,
                    stationSimulationHeaders: []
                });
            } else {
                set({
                    stationSimulationSummary: simulationData,
                    invalidStationSimulationSummary: false,
                    stationSimulationHeaders: Object.keys(simulationData[0])
                });
            }
        },

        fetchStationSimulationSummary: async (summaryId: string | null) => {
            set({stationSimulationLoaded: false});
            getStationSimulationSummary(summaryId).then((simulationData) => {
                get().setStationSimulationSummary(simulationData);
                set({stationSimulationLoaded: true });
            });
        },


        /**
         * Fetches map pack validation summary from the API
         */
        fetchMapPackValidationSummary: async (region: string) => {
            set({
                mapPackValidationLoaded: false,
                mapPackValidationSummary: [],
                invalidMapPackValidationSummary: false,
                currentValidationRegion: region
            });
            
            getMapPackValidationSummary(region).then((recostingData) => {
                if (get().currentValidationRegion === region) {
                    get().setMapPackValidationSummary(recostingData);
                    set({mapPackValidationLoaded: true });
                }       
            }).catch((error) => {
                if (get().currentValidationRegion === region) {
                    console.error("Error fetching map pack validation summary:", error);
                    set({
                        mapPackValidationSummary: [],
                        invalidMapPackValidationSummary: true,
                        mapPackValidationLoaded: true
                    });
                }
            });
        },

        // MCM validation status
        mcmValidationStatus: {} as Record<string, boolean>,

        /**
         * Fetches MCM validation status for given RSM version
         * @param rsmVersion RSM version to check
         */
        fetchMCMValidation: async (rsmVersion: string) => {
            try {
                const mcmExists = await checkMCMExists(rsmVersion);
                set((state: any) => ({
                    mcmValidationStatus: {
                        ...state.mcmValidationStatus,
                        [rsmVersion]: mcmExists
                    }
                }));
            } catch (error) {
                console.error("Error checking MCM status:", error);
                set((state: any) => ({
                    mcmValidationStatus: {
                        ...state.mcmValidationStatus,
                        [rsmVersion]: false
                    }
                }));
            }
        },

        /**
         * Gets MCM validation status for given RSM version
         * @param rsmVersion RSM version to check
         * @returns boolean indicating if MCM URL exists
         */
        getMCMValidationStatus: (rsmVersion: string) => {
            return get().mcmValidationStatus[rsmVersion] || false;
        },

        
    }));

const DEFAULT_STATION_RECOSTING_SUMMARY_PREFERENCES_KEY = "DEFAULT_STATION_RECOSTING_SUMMARY_PREFERENCES";
const DEFAULT_MAP_PACK_VALIDATION_SUMMARY_PREFERENCES_KEY = "DEFAULT_MAP_PACK_VALIDATION_SUMMARY_PREFERENCES";
const DEFAULT_MAP_PACK_RELEASE_SUMMARY_PREFERENCES_KEY = "DEFAULT_MAP_PACK_RELEASE_SUMMARY_PREFERENCES";
const DEFAULT_STATION_SIMULATION_SUMMARY_PREFERENCES_KEY = "DEFAULT_STATION_SIMULATION_SUMMARY_PREFERENCES";

/**
 * Saves the last set of table preferences used.
 *
 * @param preferences set of preferences to save
 */
const saveDefaultPreferences = (
    preferences: any,
    type: PreferencesType,
) => {
    switch (type) {
        case PreferencesType.StationRecostingSummary:
            localStorage.setItem(
                DEFAULT_STATION_RECOSTING_SUMMARY_PREFERENCES_KEY,
                JSON.stringify(preferences)
            );
            break;
        case PreferencesType.MapPackValidationSummary:
            localStorage.setItem(
                DEFAULT_MAP_PACK_VALIDATION_SUMMARY_PREFERENCES_KEY,
                JSON.stringify(preferences)
            );
            break;
        case PreferencesType.MapPackReleaseSummary:
            localStorage.setItem(
                DEFAULT_MAP_PACK_RELEASE_SUMMARY_PREFERENCES_KEY,
                JSON.stringify(preferences)
            );
            break;
        case PreferencesType.StationSimulationSummary:
            localStorage.setItem(
                DEFAULT_STATION_SIMULATION_SUMMARY_PREFERENCES_KEY,
                JSON.stringify(preferences)
            );
            break;
    }
};

/**
 * Saves the map pack classifications to localStorage
 * 
 * @param classifications Map of mapPackVersion to classification
 */
const saveMapPackClassifications = (classifications: Record<string, string>) => {
    localStorage.setItem(
        MAP_PACK_CLASSIFICATIONS_KEY,
        JSON.stringify(classifications)
    );
};

enum PreferencesType {
    StationRecostingSummary,
    MapPackValidationSummary,
    MapPackReleaseSummary,
    StationSimulationSummary,
}
